import "../../moneyReports.css"
import { useState, useEffect, useContext } from "react"
import AuthContext from "contexte/AuthContext"
import { useTranslation } from "react-i18next"
import DashboardLayout from "components/layout/dashboard/DashboardLayout"
import FilterComponent from "components/Filters/FilterComponent/FilterComponent"
import PageLoader from "components/pageLoader/PageLoader"
import Tableau from "components/DataComponents/Tableau/Tableau"
import { fetchProfits } from "services/fetchMoneyReports"
import { RELEASE_DATE } from 'utils/date';
import { Amount, Count, EmptyState, ErrorState, Pager, ReportHeader, SummaryTiles, formatEur } from "../../MoneyReportParts"
import { formatNumberWithSpaces } from "utils/numbers"

const COLUMNS = [
    { key: 'platform', label: 'Trade classique' },
    { key: 'fx', label: 'FX' },
    { key: 'structured_products', label: 'Structurés' },
    { key: 'playlist_unlock', label: 'Déblocage Playlists' },
    { key: 'commissions_margin', label: 'Margin' },
    { key: 'spread', label: 'Spread' },
    { key: 'margin', label: 'Overnight' },
    { key: 'paradox', label: 'Abonnement' },
    { key: 'withdraws', label: 'Retraits' },
]

const Profits = () => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();

    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [rowsCount, setRowsCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedDate, setSelectedDate] = useState({
        date1: { startDate: new Date(RELEASE_DATE), endDate: new Date() },
        date2: { startDate: null, endDate: null },
    });
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);
    const [timeframe, setTimeframe] = useState('day')

    const fetchData = async () => {
        try {
            setError(false);
            const response = await fetchProfits({ user, limit, page, startDate: selectedDate.date1.startDate, endDate: selectedDate.date1.endDate, timeframe });
            // la ligne Total (WITH ROLLUP) n'arrive qu'en tête de la première page
            const totalRow = response.find(row => row.index_type === 0);
            if (totalRow) setTotals(totalRow);
            else if (page === 0) setTotals(null);
            setRows(response.filter(row => row.index_type !== 0));
            setRowsCount(response.length);
        } catch (e) {
            console.log(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedDate.date1.startDate !== null && selectedDate.date1.endDate !== null) {
            fetchData();
        }
    }, [selectedDate, page, limit, timeframe]);

    const handleDate = (newDate) => {
        setSelectedDate(newDate);
        setPage(0);
    }

    const handleLimit = (newLimit) => {
        setLimit(newLimit);
        setPage(0);
    }

    const handleTimeframe = (newTimeframe) => {
        setTimeframe(newTimeframe);
        setPage(0);
    }

    const tbody = rows.map(item => ({
        data: [
            <span className="money_report_num money_report_date">{item.date_formatted}</span>,
            <Amount value={item.profit} strong />,
            ...COLUMNS.map(column => <Amount value={item[column.key]} />),
            <Count value={item.active_users} />,
            <Amount value={item.profit_per_user} />,
            <Amount value={item.referral_fees} />,
            <Amount value={item.sales_fees} />
        ]
    }));

    const tiles = totals ? [
        { label: 'Profit sur la période', value: formatEur(totals.profit), main: true },
        { label: 'Utilisateurs actifs', value: formatNumberWithSpaces(totals.active_users ?? 0) },
        { label: 'Profit par utilisateur', value: formatEur(totals.profit_per_user) },
        { label: 'Commissions Partner', value: formatEur(totals.referral_fees) },
        { label: 'Commissions Sales', value: formatEur(totals.sales_fees) },
    ] : [];

    const dropdowns = [{
        label: 'Période',
        value: timeframe,
        setter: handleTimeframe,
        options: [{ value: 'day', display: 'Jour' }, { value: 'month', display: 'Mois' }]
    }]

    return (
        <DashboardLayout page={t("pages_name.report_profit")}>
            <div className="money_report_page">
                <ReportHeader
                    title="Profits"
                    hint="Revenus de Goliaths par jour ou par mois, par source : trade, FX, produits structurés, playlists, margin, spread, overnight, abonnements et frais de retrait."
                />

                <div className="money_report_toolbar">
                    <FilterComponent
                        date={true}
                        limit={true}
                        fetchLimit={limit}
                        setFetchLimit={handleLimit}
                        selectedDate={selectedDate}
                        setSelectedDate={handleDate}
                        useDropdown={true}
                        dropdowns={dropdowns}
                    />
                </div>

                {
                    loading ? (
                        <div className='pageLoaderBox'>
                            <PageLoader />
                        </div>
                    ) : error ? (
                        <ErrorState title="Erreur lors du chargement des profits" onRetry={fetchData} />
                    ) : (
                        <>
                            {totals && <SummaryTiles tiles={tiles} />}
                            <div className="money_report_card">
                                {
                                    tbody.length || page > 0 ? (
                                        <>
                                            <Tableau
                                                thead={['Date', 'Total', ...COLUMNS.map(column => column.label), 'Utilisateurs actifs', 'Profit/User', 'Commissions Partner', 'Commissions Sales']}
                                                tbody={tbody}
                                            />
                                            <Pager page={page} setPage={setPage} hasNextPage={rowsCount >= limit} />
                                        </>
                                    ) : (
                                        <EmptyState title="Aucun profit sur la période" hint="Élargissez les dates pour voir les profits." />
                                    )
                                }
                            </div>
                        </>
                    )
                }
            </div>
        </DashboardLayout>
    )
}

export default Profits
